import type { QueryClient } from '@tanstack/react-query';

type PulseAiAccessListener = (organizationId: string | null) => void;

const listeners = new Set<PulseAiAccessListener>();

/** Offices with lost access waiting for the next microtask, per client; null: unknown office. */
const pending = new WeakMap<QueryClient, Set<string | null>>();

/**
 * Subscribes to lost access in an office (null: unknown office). The open
 * chat reloads its thread, whose answers the server may just have withdrawn.
 * Returns the unsubscribe.
 */
export function onPulseAiAccessLost(listener: PulseAiAccessListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Transcripts, thread previews and resolved titles, legacy or gated; plans hold run proposals. */
function isWithdrawableKey(queryKey: readonly unknown[], offices: Set<string | null>) {
  if (queryKey[0] !== 'pulse-ai') {
    return false;
  }
  const inOffice = (organizationId: unknown) =>
    offices.has(null) || offices.has(organizationId as string);
  if (queryKey[1] === 'messages' || queryKey[1] === 'threads') {
    return inOffice(queryKey[2]);
  }
  if (queryKey[1] === 'gated') {
    return (
      ['messages', 'threads', 'titles'].includes(queryKey[4] as string) && inOffice(queryKey[3])
    );
  }
  return queryKey[1] === 'plan-run';
}

function callSafely(run: () => void): void {
  try {
    run();
  } catch (error) {
    queueMicrotask(() => {
      throw error;
    });
  }
}

function flush(queryClient: QueryClient): void {
  const offices = pending.get(queryClient);
  pending.delete(queryClient);
  if (!offices) {
    return;
  }
  queryClient
    .resetQueries({ predicate: ({ queryKey }) => isWithdrawableKey(queryKey, offices) })
    .catch(() => {
      /* eviction is best-effort; the next read returns the withdrawn text regardless */
    });
  const notified = offices.has(null) ? [null] : [...offices];
  for (const organizationId of notified) {
    for (const listener of listeners) {
      callSafely(() => listener(organizationId));
    }
  }
}

/**
 * A revoke withdraws the person's Pulse AI answers that drew on the thread
 * (sharing plan U5); cached transcripts, previews and titles still hold the
 * old text, so they are reset and the open chat is told to reload. Revokes in
 * one tick (a reconciliation diff) coalesce into one reset and one reload.
 */
export function evictPulseAiAfterLostAccess(
  queryClient: QueryClient,
  organizationId: string | null,
): void {
  const offices = pending.get(queryClient);
  if (offices) {
    offices.add(organizationId);
    return;
  }
  pending.set(queryClient, new Set([organizationId]));
  queueMicrotask(() => flush(queryClient));
}
